import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Pressable,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { Clock, AlertTriangle, RefreshCw, Trash2 } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { Card, useConfirm } from '@components/common';
import { useOutboxStore } from '../../store/outboxStore';
import type { OutboxObservationItem } from '../../api/outbox/types';
import { formatRelativeTimeFromMillis } from '../../utils/time';

interface PendingObservationsProps {
  // When set, only items linked to this structure render — used on the
  // structure thread. Omitted on the feed, where every one of the viewer's
  // pending items shows.
  structureId?: string;
  // Extra styling for the outer container — e.g. the feed screen adds
  // horizontal padding to match PostCard's own marginHorizontal; the
  // structure thread's card already supplies that via its own padding.
  style?: StyleProp<ViewStyle>;
}

/**
 * Compact "waiting to upload" banner + one card per queued observation from
 * the durable offline outbox (see docs/plans — Durable Offline Outbox).
 * Renders nothing when there are no items for this viewer/scope — zero
 * visual change for the common case. Shared by the feed screen and the
 * structure thread (RecentActivitySection).
 */
export function PendingObservations({ structureId, style }: PendingObservationsProps) {
  const { colors } = useTheme();
  const items = useOutboxStore((s) => s.items);
  const retry = useOutboxStore((s) => s.retry);
  const remove = useOutboxStore((s) => s.remove);
  const confirm = useConfirm();

  const filtered = useMemo(
    () => (structureId ? items.filter((it) => it.data.structureId === structureId) : items),
    [items, structureId]
  );

  if (filtered.length === 0) return null;

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: 'Delete pending observation?',
      message: "This photo hasn't uploaded yet. Deleting it cannot be undone.",
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (confirmed) void remove(id);
  };

  return (
    <View style={[styles.container, style]}>
      <View style={styles.banner} testID="pending-observations-banner">
        <Clock size={13} color={colors.textTertiary} />
        <Text style={[styles.bannerText, { color: colors.textTertiary }]}>
          Waiting to upload ({filtered.length})
        </Text>
      </View>
      <View style={styles.list}>
        {filtered.map((item) => (
          <PendingCard
            key={item.id}
            item={item}
            onRetry={() => void retry(item.id)}
            onDelete={() => void handleDelete(item.id)}
          />
        ))}
      </View>
    </View>
  );
}

function PendingCard({
  item,
  onRetry,
  onDelete,
}: {
  item: OutboxObservationItem;
  onRetry: () => void;
  onDelete: () => void;
}) {
  const { colors } = useTheme();
  const failed = item.status === 'failed';
  const relativeTime = formatRelativeTimeFromMillis(item.createdAtMs);
  const captionSnippet = item.data.caption?.trim() || 'Observation';

  return (
    <Card
      variant="filled"
      padding="sm"
      style={styles.card}
      testID={`pending-observation-card-${item.id}`}>
      <View style={styles.cardRow}>
        <View style={styles.cardBody}>
          <Text style={[styles.caption, { color: colors.text }]} numberOfLines={1}>
            {captionSnippet}
          </Text>
          <View style={styles.statusRow}>
            {failed ? (
              <AlertTriangle size={12} color={colors.danger} />
            ) : (
              <ActivityIndicator size="small" color={colors.textTertiary} />
            )}
            <Text
              style={[styles.statusText, { color: failed ? colors.danger : colors.textTertiary }]}
              numberOfLines={1}>
              {failed ? 'Failed to upload' : 'Waiting to upload'}
              {relativeTime ? ` · ${relativeTime}` : ''}
            </Text>
          </View>
        </View>
        {failed && (
          <View style={styles.actions}>
            <Pressable
              onPress={onRetry}
              accessible={false}
              hitSlop={8}
              style={({ pressed }) => [
                styles.actionButton,
                { backgroundColor: colors.surfaceElevated },
                pressed && { opacity: 0.6 },
              ]}
              accessibilityRole="button"
              accessibilityLabel="Retry upload">
              <RefreshCw size={14} color={colors.text} />
            </Pressable>
            <Pressable
              onPress={onDelete}
              accessible={false}
              hitSlop={8}
              style={({ pressed }) => [
                styles.actionButton,
                { backgroundColor: colors.dangerSoft },
                pressed && { opacity: 0.6 },
              ]}
              accessibilityRole="button"
              accessibilityLabel="Delete pending observation">
              <Trash2 size={14} color={colors.danger} />
            </Pressable>
          </View>
        )}
      </View>
    </Card>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: SPACING.md,
  },
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    marginBottom: SPACING.sm,
  },
  bannerText: {
    ...TYPOGRAPHY.overline,
  },
  list: {
    gap: SPACING.sm,
  },
  card: {},
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  cardBody: {
    flex: 1,
  },
  caption: {
    ...TYPOGRAPHY.bodySmall,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    marginTop: 2,
  },
  statusText: {
    ...TYPOGRAPHY.caption,
    flexShrink: 1,
  },
  actions: {
    flexDirection: 'row',
    gap: SPACING.xs,
  },
  actionButton: {
    width: 28,
    height: 28,
    borderRadius: BORDER_RADIUS.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
